import React, { useState } from 'react';
import { apiPost } from '../../api/api';

interface JoinModalProps {
  groupId: string;
  groupName: string;
  community?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function JoinModal({ groupId, groupName, community, onClose, onSuccess }: JoinModalProps) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiPost('/api_join_group', { group_id: groupId, community, message }, true);
      setSent(true);
      setTimeout(() => onSuccess(), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(26,31,54,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: '2rem',
          width: '100%',
          maxWidth: '460px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
          border: '1px solid #eef0f6',
        }}
      >
        {sent ? (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#f0fdf4',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem',
                fontSize: '2rem',
              }}
            >
              ✅
            </div>
            <h3 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '1.15rem', margin: '0 0 0.5rem' }}>
              Request Sent!
            </h3>
            <p style={{ color: '#6b7a99', fontSize: '0.9rem', margin: 0, lineHeight: 1.6 }}>
              You'll be notified when a group member approves your request.
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '1.1rem', margin: '0 0 0.25rem' }}>
                  Join {groupName}
                </h2>
                <p style={{ color: '#8896ab', fontSize: '0.825rem', margin: 0 }}>
                  Your request will be reviewed by the group
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8896ab',
                  fontSize: '1.4rem',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: 1,
                  marginLeft: '1rem',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label
                  style={{
                    display: 'block',
                    color: '#4a5568',
                    fontSize: '0.875rem',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Introduce yourself or explain why you'd like to join..."
                  rows={4}
                  style={{
                    width: '100%',
                    backgroundColor: '#f7f8fc',
                    border: '1.5px solid #e2e6f0',
                    borderRadius: '10px',
                    padding: '0.875rem',
                    color: '#1a1f36',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    lineHeight: 1.6,
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')}
                />
              </div>

              {error && (
                <p
                  style={{
                    color: '#c53030',
                    fontSize: '0.85rem',
                    margin: '0 0 1rem',
                    padding: '0.625rem 0.875rem',
                    backgroundColor: '#fff5f5',
                    borderRadius: '8px',
                    border: '1px solid #fed7d7',
                  }}
                >
                  {error}
                </p>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    backgroundColor: '#f7f8fc',
                    border: '1.5px solid #e2e6f0',
                    borderRadius: '10px',
                    color: '#4a5568',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#c5cce0')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    flex: 2,
                    padding: '0.875rem',
                    backgroundColor: '#FF385C',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.75 : 1,
                    transition: 'all 0.15s',
                    boxShadow: '0 2px 8px rgba(255,56,92,0.3)',
                  }}
                >
                  {submitting ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
